import store from '../store/store';
import * as RoleActions from '../store/actions/roleActions';
import {getCurrentUserId} from "../lib/picos/currentLogin";
import newHRTimestamp from "../lib/picos/timestamp";
import {UserRoles, uuid} from "../@types/schema";
import Role from "../entities/role";
import Contact from "../entities/contact";

export const LEGAL_ROLES: UserRoles[] = ['Paralegal', 'Attorney', 'Legal Secretary'];

// Roles that can view the Payments Dashboard (OWA-515). Shared by the
// Financials page gate and the nav-menu link so they can never disagree.
export const PAYMENTS_DASHBOARD_ROLES: UserRoles[] = ['Owner', 'Administrator', 'Bookkeeper', 'Office Manager'];

export const ALL_RELATED = ['lead', 'matter', 'address', 'answer', 'bankAccount', 'caseType', 'contact', 'document',
    'event', 'expense', 'firm', 'identification', 'lookup', 'participant',
    'question', 'referral', 'resource', 'transaction', 'user'
];

/**
 * Get all roles for a user (assumes current user)
 */
export const getRoles = (userId?: uuid): Role[] => {
    const activeRoles = (store.getState().roles).filter(r => r.active);
    if (userId) return activeRoles.filter(r => r.users.includes(userId));
    else return activeRoles;
};

export const hasLegalRole = (userId: uuid) => {
    const userRoleNames = getRoles(userId).map(r => r.name);
    return LEGAL_ROLES.some(lr => userRoleNames.includes(lr));
};

/**
 * Gets all roles for the currently logged-in user
 */
export const getMyRoles = (userId: uuid = null): Role[] => {
    const id = userId || getCurrentUserId();
    return getRoles(id);
};

/**
 * Checks to see if the given user has a specific role
 */
export const hasRole = (userId: uuid, role: UserRoles): boolean => {
    return !!getRoles(userId).find(r => r.name === role);
};

/**
 * Checks to see if the given user has specific roles.
 */
export const hasRoles = (userId: uuid, roles: UserRoles[], mustHaveAll: boolean): boolean => {
    if (mustHaveAll) return roles.every(role => getRoles(userId).find(r => r.name === role));
    else return roles.some(role => getRoles(userId).find(r => r.name === role));
};

/**
 * Checks to see if the given user has a specific roleId
 */
export const hasRoleId = (userId: uuid, roleId: uuid): boolean => {
    return !!getRoles(userId).find(r => r.id === roleId);
};

/**
 * Checks to see if the given user is an Administrator. If no user given, use current user.
 */
export const isAdmin = (userId?: uuid) => {
    if (!userId) userId = getCurrentUserId();
    return hasRole(userId, 'Administrator') || hasRole(userId, 'Owner');
};

/**
 * Is the user in the given group? If no group given, use current user.
 */
export const isInGroup = (groupId: uuid, userId?: uuid): boolean => {
    const group = store.getState().groups.get(groupId);
    if (!group) {
        console.warn('🔑 Auth: No matching group found', groupId, userId);
        return false;
    }
    else {
        return group.userIds.includes(userId || getCurrentUserId());
    }
};

/**
 * Checks to see if a given user is an Owner
 */
export const isOwner = (userId?: uuid): boolean => {
    if (!userId) userId = getCurrentUserId();
    return hasRole(userId, 'Owner');
};

export const isUser = (contact: Contact): boolean => {
    if (!contact) return false;
    return store.getState().users.some(u => u.contactId === contact.id);
};

/**
 * Grant a named role to a specified userId
 */
export const grantRole = (userId: uuid, roleName: string) => {
    const role = getRoles().find(r => r.name === roleName);

    if (!role) throw new Error(`Could not find ${roleName} role`);
    else {
        const roleClone = role.clone();
        roleClone.addedOn = newHRTimestamp();
        roleClone.addedBy = getCurrentUserId();

        if (!roleClone.users.includes(userId)) {
            roleClone.users = roleClone.users.concat([userId]);
            store.dispatch(RoleActions.saveRoleQueue(roleClone));
        }
    }
};

/**
 * Grant a roleId to a specified userId
 */
export const grantRoleId = (userId: uuid, roleId: uuid) => {
    // OWA-834: a missing userId used to be pushed into role.users as-is and
    // persisted as a NULL member, silently replacing a correct assignment.
    // Fail loudly instead; the role row must never carry a null user.
    if (!userId) throw new Error(`Cannot grant role ${roleId}: no userId was supplied`);

    const role = getRoles().find(r => r.id === roleId);

    if (!role) throw new Error(`Could not find role ${roleId}`);
    else {
        const roleClone = role.clone();
        roleClone.addedOn = newHRTimestamp();
        roleClone.addedBy = getCurrentUserId();

        if (!roleClone.users.includes(userId)) {
            roleClone.users.push(userId);
            store.dispatch(RoleActions.saveRoleQueue(roleClone));
        }
    }
};

/**
 * Revoke a role from a specified userId
 */
export const revokeRole = (userId: uuid, roleName: UserRoles) => {
    const role = getRoles().find(r => r.name === roleName);

    if (!role) throw new Error(`Could not find ${roleName} role`);
    else {
        const roleClone = role.clone();
        roleClone.addedOn = newHRTimestamp();
        roleClone.addedBy = getCurrentUserId();

        if (roleClone.users.includes(userId)) {
            roleClone.users = roleClone.users.filter(uid => uid !== userId);
            store.dispatch(RoleActions.saveRoleQueue(roleClone));
        }
    }
};

/**
 * Revoke a roleId from a specified userId
 */
export const revokeRoleId = (userId: uuid, roleId: uuid) => {
    const role = getRoles().find(r => r.id === roleId);

    if (!role) throw new Error(`Could not find role ${roleId}`);
    else {
        const roleClone = role.clone();
        roleClone.addedOn = newHRTimestamp();
        roleClone.addedBy = getCurrentUserId();

        if (roleClone.users.includes(userId)) {
            roleClone.users = roleClone.users.filter(uid => uid !== userId);
            store.dispatch(RoleActions.saveRoleQueue(roleClone));
        }
    }
};
